"use client";

import { AxiosError } from "axios";
import { useRouter } from "next/navigation";
import React, {
  useCallback,
  useEffect,
  createContext,
  useContext,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import useUpdateAcoData from "@/hooks/api/quote/use-update-aco";
import { RootState } from "@/store/root-reducer";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { OptionalExtra } from "@/types/OptionalExtraType";
import handleError, { ErrorResponse } from "@/utils/handle-errors";

import { useRenewalStateContext } from "./renewal-state-provider";

interface PolicyExtrasActionContextType {
  optionalExtrasWithModifiedState: OptionalExtra[];
  setOptionalExtrasWithModifiedState: React.Dispatch<
    React.SetStateAction<OptionalExtra[]>
  >;
  handlePolicyExtrasSubmit: () => Promise<void>;
  handleOptionalExtrasStateChange: (
    code: string,
    isSelected: boolean,
    value?: string
  ) => void;
  onChangeGolfCoverTravelersCount: (value: string, code?: string) => void;
  setIsGolfModification: React.Dispatch<React.SetStateAction<boolean>>;
  isGolfModification: boolean;
  isShowCTABtnLoading: boolean;
}

const PolicyExtrasStateContext = createContext<PolicyExtrasActionContextType>({
  optionalExtrasWithModifiedState: [],
  setOptionalExtrasWithModifiedState: () => {},
  handlePolicyExtrasSubmit: () => Promise.resolve(),
  handleOptionalExtrasStateChange: () => {},
  onChangeGolfCoverTravelersCount: () => {},
  setIsGolfModification: () => {},
  isGolfModification: false,
  isShowCTABtnLoading: false,
});

type RenewalStateProviderProps = {
  children: React.ReactNode;
  isLoading: boolean;
};

function PolicyExtrasActionProvider({
  children,
}: Readonly<RenewalStateProviderProps>) {
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { priceResult } = attributes;
  const { updateAcoData, data, loading, error } = useUpdateAcoData();
  const [isGolfModification, setIsGolfModification] = React.useState(false);
  const router = useRouter();
  const { current } = useWizard();
  const dispatch = useDispatch();

  // This will keep the optional extras with modified state
  const [optionalExtrasWithModifiedState, setOptionalExtrasWithModifiedState] =
    React.useState<OptionalExtra[]>([]);
  const [isShowCTABtnLoading, setIsShowCTABtnLoading] = React.useState(false);
  const { setExpandedSections } = useRenewalStateContext();

  useEffect(() => {
    if (!loading && data && !error) {
      setIsShowCTABtnLoading(false);
      dispatch(updateQuote(data));
    } else if (!loading && error !== null) {
      setIsGolfModification(false);
      setIsShowCTABtnLoading(false);
      handleError(error as AxiosError<ErrorResponse>, router, dispatch);
    }
  }, [data, loading, error, dispatch, router]);

  const onChangeGolfCoverTravelersCount = useCallback(
    (value: string, code?: string) => {
      if (!priceResult) return;

      const updatedExtras = priceResult?.optionalExtras.map((extra) => {
        if (extra.code === code) {
          return {
            ...extra,
            price: [
              {
                ...extra.price[0],
                selected: value !== "0",
                detail: {
                  description: null,
                  value,
                },
              },
            ],
          };
        }
        return extra;
      });
      updateAcoData(updatedExtras);
    },
    [priceResult, updateAcoData]
  );

  const handleOptionalExtrasStateChange = useCallback(
    (code: string, isSelected: boolean, value?: string) => {
      const updatedExtras = optionalExtrasWithModifiedState.map((extra) => {
        if (code === "GOLF_EQUIPMENT" && extra.code === code && value) {
          return {
            ...extra,
            price: [
              {
                ...extra.price[0],
                selected: isSelected,
                detail: {
                  description: null,
                  value,
                },
              },
            ],
          };
        }
        if (extra.code === code) {
          return {
            ...extra,
            price: [
              {
                ...extra.price[0],
                selected: isSelected,
              },
            ],
          };
        }
        return extra;
      });

      setOptionalExtrasWithModifiedState(updatedExtras);
    },
    [optionalExtrasWithModifiedState, setOptionalExtrasWithModifiedState]
  );

  const handlePolicyExtrasSubmit = useCallback(async () => {
    if (
      !optionalExtrasWithModifiedState ||
      optionalExtrasWithModifiedState.length === 0
    )
      return;
    setIsShowCTABtnLoading(true);
    await updateAcoData(optionalExtrasWithModifiedState, false);
    setExpandedSections(["policy-details"]);
    router.push(current.next ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    optionalExtrasWithModifiedState,
    router,
    setExpandedSections,
    updateAcoData,
  ]);

  const value = useMemo(
    () => ({
      optionalExtrasWithModifiedState,
      setOptionalExtrasWithModifiedState,
      handlePolicyExtrasSubmit,
      handleOptionalExtrasStateChange,
      onChangeGolfCoverTravelersCount,
      setIsGolfModification,
      isGolfModification,
      isShowCTABtnLoading,
    }),
    [
      handleOptionalExtrasStateChange,
      handlePolicyExtrasSubmit,
      isGolfModification,
      isShowCTABtnLoading,
      onChangeGolfCoverTravelersCount,
      optionalExtrasWithModifiedState,
    ]
  );

  return (
    <PolicyExtrasStateContext.Provider value={value}>
      {children}
    </PolicyExtrasStateContext.Provider>
  );
}

export const usePolicyExtrasAction = () => useContext(PolicyExtrasStateContext);

export default PolicyExtrasActionProvider;
